import { useCallback, useMemo, useState } from 'react';

import {
	Icons,
	PageTitle,
	Table,
	UnavailableFeatureMessage,
} from '@components/common';
import Modal from '@components/common/Modal';

import { useMutateUpdateRUConfiguration } from '@hooks/cmModule/ruConfigurations/useMutateUpdateRUConfiguration';
import { useQueryRUConfigurationConfigs } from '@hooks/cmModule/ruConfigurations/useQueryRUConfigurationConfigs';
import { useQueryRUConfigurationMiddlewares } from '@hooks/cmModule/ruConfigurations/useQueryRUConfigurationMiddlewares';
import { useQueryRUConfigurationNetconfs } from '@hooks/cmModule/ruConfigurations/useQueryRUConfigurationNetconfs';
import { useQueryRUConfigurations } from '@hooks/cmModule/ruConfigurations/useQueryRUConfigurations';
import { useQueryRUConfigurationStatuses } from '@hooks/cmModule/ruConfigurations/useQueryRUConfigurationStatuses';
import { useQueryRUConfigurationUsers } from '@hooks/cmModule/ruConfigurations/useQueryRUConfigurationUsers';
import { useVendorDrivenFeatureAvailability } from '@hooks/utils/useVendorDrivenFeatureAvailability';

import { auth, configurationManagement } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { ICreateRUConfigurationsDTO } from '@interfaces/cmModule/dto/ruConfigurationsDTO';
import { IRUConfiguration } from '@interfaces/cmModule/ruConfiguration';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta, EAdminPanelPageTitles } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { MODAL_WIDTH_580 } from '@constants/modals';
import { TableFilterTypes } from '@constants/table';
import { DEFAULT_CONFIGURATION_MIMO, DEFAULT_CONFIGURATION_STATUS } from './constants';

import RUConfigurationFormTabs from './RUConfigurationFormTabs';
import { getColumns } from './utils';
import ViewRUConfigurationModal from './ViewRUConfigurationModal';

import styles from './RUConfigurationManagement.module.scss';

const RUConfigurationManagement = () => {
	const {
		accessPermission: {
			networkOperatorCmModuleAdmin,
			networkOperatorCmModuleEditor,
		},
	} = useAppSelector(auth);

	const { selectedVendor } = useAppSelector(configurationManagement);

	const [selectedRUConfiguration, setSelectedRUConfiguration] = useState<IRUConfiguration | undefined>();

	const [isConfigurationFormModal, setIsConfigurationFormModal] = useState(false);
	const [isViewModal, setIsViewModal] = useState(false);
	const [isErrorResultModal, setIsErrorResultModal] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);

	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [searchValue, setSearchValue] = useState('');
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [selectedConfiguration, setSelectedConfiguration] = useState<TSelectOption<string>>(null);
	const [selectedMiddleware, setSelectedMiddleware] = useState<TSelectOption<string>>(null);
	const [selectedUser, setSelectedUser] = useState<TSelectOption<string>>(null);
	const [selectedNetconf, setSelectedNetconf] = useState<TSelectOption<string>>(null);
	const [selectedStatus, setSelectedStatus] = useState<TSelectOption<string>>(null);

	const isViewerOnly = !networkOperatorCmModuleEditor && !networkOperatorCmModuleAdmin;
	const isFeatureAvailable = useVendorDrivenFeatureAvailability();

	const openConfigurationFormModal = useCallback((configuration: IRUConfiguration, isEditMode = false) => {
		if (configuration) {
			setSelectedRUConfiguration(configuration);
		}

		setIsEditMode(isEditMode);
		setIsConfigurationFormModal(true);
	}, []);

	const openViewErrorResultModal = useCallback((configuration: IRUConfiguration) => {
		if (configuration) {
			setSelectedRUConfiguration(configuration);
		}

		setIsErrorResultModal(true);
	}, []);

	const closeConfigurationModal = useCallback(() => {
		setSelectedRUConfiguration(undefined);
		setIsConfigurationFormModal(false);
		setIsEditMode(false);
		setIsDirtyFormModal(false);
	}, []);

	const closeViewErrorResultModal = useCallback(() => {
		setSelectedRUConfiguration(undefined);
		setIsErrorResultModal(false);
	}, []);

	const {
		ruConfigurations,
		countOfRecords,
		isLoadingRUConfigurations,
	} = useQueryRUConfigurations({
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		lastChangedById: selectedUser?.value,
		middlewareId: selectedMiddleware?.value,
		netconfApplyResult: selectedNetconf?.value,
		status: selectedStatus?.value,
		netconfConfiguratedStatus: selectedConfiguration?.value,
	});

	const {
		ruConfigurationStatuses,
		isLoadingRUConfigurationStatuses,
	} = useQueryRUConfigurationStatuses();

	const {
		ruConfigurationUsers,
		isLoadingRUConfigurationUsers,
	} = useQueryRUConfigurationUsers();

	const {
		ruConfigurationNetconfs,
		isLoadingRUConfigurationNetconfs,
	} = useQueryRUConfigurationNetconfs();

	const {
		ruConfigurationMiddlewares,
		isLoadingRUConfigurationMiddlewares,
	} = useQueryRUConfigurationMiddlewares();

	const {
		ruConfigurationConfigs,
		isLoadingRUConfigurationConfigs,
	} = useQueryRUConfigurationConfigs();

	const {
		updateRUConfiguration,
		isLoadingUpdateRUConfiguration,
	} = useMutateUpdateRUConfiguration({
		onSuccess: closeConfigurationModal,
	});

	const handleMiddlewareChange = useCallback((option: TSelectOption<string>) => {
		setSelectedMiddleware(option);
		setPage(defaultMeta.page);
	}, []);

	const handleConfigurationChange = useCallback((option: TSelectOption<string>) => {
		setSelectedConfiguration(option);
		setPage(defaultMeta.page);
	}, []);

	const handleUserChange = useCallback((option: TSelectOption<string>) => {
		setSelectedUser(option);
		setPage(defaultMeta.page);
	}, []);

	const handleStatusChange = useCallback((option: TSelectOption<string>) => {
		setSelectedStatus(option);
		setPage(defaultMeta.page);
	}, []);

	const handleNetconfChange = useCallback((option: TSelectOption<string>) => {
		setSelectedNetconf(option);
		setPage(defaultMeta.page);
	}, []);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'middleware',
				placeholder: 'Middleware',
				option: selectedMiddleware,
				options: ruConfigurationMiddlewares,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleMiddlewareChange,
				defaultValue: null,
			},
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedStatus,
				options: ruConfigurationStatuses,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleStatusChange,
				defaultValue: null,
			},
			{
				name: 'configuration',
				placeholder: 'Configuration',
				option: selectedConfiguration,
				options: ruConfigurationConfigs,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleConfigurationChange,
				defaultValue: null,
			},
			{
				name: 'netconf',
				placeholder: 'Netconf apply result',
				option: selectedNetconf,
				options: ruConfigurationNetconfs,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleNetconfChange,
				defaultValue: null,
			},
			{
				name: 'user',
				placeholder: 'User',
				option: selectedUser,
				options: ruConfigurationUsers,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleUserChange,
				defaultValue: null,
			},
		];
	}, [
		selectedMiddleware,
		selectedStatus,
		selectedConfiguration,
		selectedUser,
		selectedNetconf,
		ruConfigurationMiddlewares,
		ruConfigurationStatuses,
		ruConfigurationConfigs,
		ruConfigurationUsers,
		ruConfigurationNetconfs,
		handleNetconfChange,
		handleMiddlewareChange,
		handleStatusChange,
		handleConfigurationChange,
		handleUserChange,
	]);

	const openViewConfigurationModal = useCallback((configuration: IRUConfiguration) => {
		setSelectedRUConfiguration(configuration);
		setIsViewModal(true);
	}, []);

	const closeViewModal = useCallback(() => {
		setSelectedRUConfiguration(undefined);
		setIsViewModal(false);
	}, []);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by name',
	}], [searchValue, searchHandler]);

	const columns = useMemo(() => getColumns({
		isViewerOnly,
		openConfigurationFormModal,
		openViewConfigurationModal,
		openViewErrorResultModal,
	}), [
		isViewerOnly,
		openConfigurationFormModal,
		openViewConfigurationModal,
		openViewErrorResultModal,
	]);

	const submitForm = useCallback((values: any) => {
		if (!selectedRUConfiguration) {
			return;
		}

		const dto: ICreateRUConfigurationsDTO = {
			cuPlaneVlanId: values.vlanId,
			duMacAddress: values.duMacAddress,
			ruMacAddress: values.ruMacAddress,
			txRxArrayCarriers: [
				{
					absoluteFrequencyCenter: Number(values.nrafrcn),
					activeStatus: DEFAULT_CONFIGURATION_STATUS,
					centerOfChannelBandwidth: Number(values.frequency),
					channelBandwidth: Number(values.channelBandwidth),
					gain: Number(values.transmissionGain),
				},
			],
		};

		updateRUConfiguration({
			id: selectedRUConfiguration.id,
			...dto,
		});
	}, [
		selectedRUConfiguration,
		updateRUConfiguration,
	]);

	const isLoading = isLoadingRUConfigurationConfigs
		|| isLoadingRUConfigurationMiddlewares
		|| isLoadingRUConfigurationNetconfs
		|| isLoadingRUConfigurationStatuses
		|| isLoadingRUConfigurationUsers
		|| isLoadingRUConfigurations;

	const isLoadingForm = isLoadingUpdateRUConfiguration;

	return isFeatureAvailable ? (
		<div className={styles.wrapper}>
			<PageTitle
				icon={Icons.configurationIcon()}
				title={EAdminPanelPageTitles.RUConfiguration}
			/>
			<Modal
				isOpen={isViewModal}
				onClose={closeViewModal}
				title='View configuration'
				width={MODAL_WIDTH_580}
			>
				<ViewRUConfigurationModal configuration={selectedRUConfiguration} />
			</Modal>
			<Modal
				isOpen={isErrorResultModal}
				onClose={closeViewErrorResultModal}
				title='Apply Error Results'
				width={MODAL_WIDTH_580}
			>
				<div className={styles.updateResultWrapper}>
					<div className={styles.updateResultItem}>
						<span className={styles.updateResultTitle}>CU VLAN-ID: </span>
						<span className={styles.updateResultValue}>
							{selectedRUConfiguration?.updateResult?.cuPlaneVlanIdErrorMessage || '-'}
						</span>
					</div>
					<div className={styles.updateResultItem}>
						<span className={styles.updateResultTitle}>DU MAC Address: </span>
						<span className={styles.updateResultValue}>
							{selectedRUConfiguration?.updateResult?.duMacAddressErrorMessage || '-'}
						</span>
					</div>
					<div className={styles.updateResultItem}>
						<span className={styles.updateResultTitle}>RU MAC Address: </span>
						<span className={styles.updateResultValue}>
							{selectedRUConfiguration?.updateResult?.ruMacAddressErrorMessage || '-'}
						</span>
					</div>
					<div className={styles.updateResultItem}>
						<span className={styles.updateResultTitle}>Carriers: </span>
						<span className={styles.updateResultValue}>
							{selectedRUConfiguration?.updateResult?.carriersErrorMessage || '-'}
						</span>
					</div>
				</div>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isConfigurationFormModal}
				onClose={closeConfigurationModal}
				title={isEditMode ? 'Edit configuration' : 'Add configuration'}
				width={MODAL_WIDTH_580}
			>
				<RUConfigurationFormTabs
					configurationName={selectedRUConfiguration?.networkElementName || ''}
					editableConfiguration={isEditMode ? selectedRUConfiguration : undefined}
					isLoading={isLoadingForm}
					mimoMode={selectedRUConfiguration?.mimoMode?.name || DEFAULT_CONFIGURATION_MIMO}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitForm={submitForm}
				/>
			</Modal>
			<div className={styles.tableContainer}>
				<Table
					columns={columns}
					data={ruConfigurations}
					filters={filters}
					isLoading={isLoading}
					isPinColumns
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1]}
				/>
			</div>
		</div>
	) : <UnavailableFeatureMessage vendorName={selectedVendor?.value} />;
};

export default RUConfigurationManagement;
